import React from 'react';
import { i18n } from '../../i18n/translate';
import * as Core from '../../core';
import logo from '../../images/everfi_logo.svg';

type UserData = {
  organization_name?: string;
  organization_id?: string;
  first_name?: string;
  last_name?: string;
};

interface Props {
  cookieEnabled: boolean;
  copySuccess: boolean;
  copyToClipboard: () => void;
  currentUrl: string;
  getOS: () => any;
  getUserTypes: (userData: UserData) => any;
  helpMessage: string;
  helpMessageEnabled: boolean;
  isFoundry: boolean;
  isHomeroom: boolean;
  showHelpModal: boolean;
  toggleHelpModal: () => void;
  userData?: UserData;
}

const HelpModalBody: React.FC<Props> = ({
  cookieEnabled,
  copySuccess,
  copyToClipboard,
  currentUrl,
  getOS,
  getUserTypes,
  helpMessage,
  helpMessageEnabled,
  isFoundry,
  isHomeroom,
  showHelpModal,
  toggleHelpModal,
  userData,
}) => {
  const header = (
    <Core.Layout id="help-modal-title" className="help-modal-header">
      <Core.Typography as="h2">{i18n.t('Need Help?')}</Core.Typography>
    </Core.Layout>
  );
  return (
    <Core.Modal
      isOpen={showHelpModal}
      onClose={toggleHelpModal}
      header={header}
      size="lg"
      modalType="modal"
      className="help-modal foundry"
    >
      <Core.Layout id="help-modal">
        <Core.Layout className="modal-body">
          <Core.Typography as="h3">{i18n.t('System Requirements')}</Core.Typography>
          <Core.Typography>
            {i18n.t('For best performance, use our')}{' '}
            <Core.Link href="https://everfi.com/minimum-system-requirements" target="_blank" rel="noopener noreferrer">
              {i18n.t('minimum system requirements')}
            </Core.Link>
            .
          </Core.Typography>
          <Core.Typography as="h3">{i18n.t('Technical Support')}</Core.Typography>

          {!!helpMessage && helpMessageEnabled /* Custom Support Info */ && (
            <div dangerouslySetInnerHTML={{ __html: helpMessage }} />
          )}
          {!helpMessageEnabled && isFoundry /* Foundry Default Support Info */ && (
            <Core.Typography>
              {i18n.t('You can get help by accessing our')}{' '}
              <Core.Link href="https://support.everfi.com" target="_blank" rel="noopener noreferrer">
                {i18n.t('technical support site')}
              </Core.Link>{' '}
              {i18n.t('to read FAQs or chat with our support team.')}
            </Core.Typography>
          )}
          {!helpMessageEnabled && !isFoundry && (
            <Core.Typography>
              {i18n.t('For technical support with EVERFI course content, please email:')}{' '}
              <Core.Link href={`mailto:${isHomeroom ? 'k12support' : 'learnersupport'}@everfi.com`}>
                {`${isHomeroom ? 'k12support' : 'learnersupport'}@everfi.com`}
              </Core.Link>{' '}
            </Core.Typography>
          )}

          <Core.Typography>
            {i18n.t('Please copy and paste the information below into your support message:')}
          </Core.Typography>
          <section id="help-modal-info">
            <Core.Layout>
              {i18n.t('Platform')}:{' '}
              <span className="f-500" data-info-value="platform">
                {isHomeroom ? i18n.t('Homeroom') : i18n.t('Foundry')}
              </span>{' '}
            </Core.Layout>
            {!!userData && (
              <React.Fragment>
                {!!userData.organization_name && isFoundry && (
                  <Core.Layout>
                    {i18n.t('Organization name')}:{' '}
                    <span className="f-700" data-info-value="organization_name">
                      {userData.organization_name}
                    </span>{' '}
                  </Core.Layout>
                )}
                {!!userData.organization_id && isFoundry && (
                  <Core.Layout>
                    {i18n.t('Organization ID')}:{' '}
                    <span className="f-700" data-info-value="organization_id">
                      {userData.organization_id}
                    </span>{' '}
                  </Core.Layout>
                )}
                <Core.Layout mb="md">
                  {i18n.t('Operating system')}:{' '}
                  <span className="f-700" data-info-value="operating_system">
                    {getOS()}. {i18n.t('Cookies are {{cookieEnabled}}', { cookieEnabled })}.{' '}
                    {i18n.t('Javascript is enabled')}.{' '}
                  </span>
                </Core.Layout>
                {(!!userData.first_name || !!userData.last_name) && (
                  <Core.Layout>
                    {i18n.t('Name')}:{' '}
                    <span
                      className="f-700"
                      data-info-value="user_name"
                    >{`${userData.first_name} ${userData.last_name}`}</span>{' '}
                  </Core.Layout>
                )}
                {!!getUserTypes(userData) && (
                  <Core.Layout>
                    {i18n.t('User Type')}:{' '}
                    <span className="f-700" data-info-value="user_type">
                      {getUserTypes(userData)}
                    </span>{' '}
                  </Core.Layout>
                )}
              </React.Fragment>
            )}
            <Core.Layout>
              {i18n.t('Current URL')}: <Core.Link href={currentUrl}>{currentUrl}</Core.Link>
            </Core.Layout>
          </section>
          <Core.Button
            isDisabled={copySuccess}
            variant="primary"
            colorScheme="brand"
            onClick={() => copyToClipboard()}
            data-action="copy"
            leftIcon={<Core.Icon iconName={copySuccess ? 'check' : 'copy'} />}
          >
            {copySuccess ? i18n.t('Copied successfully!') : i18n.t('Copy')}
          </Core.Button>
          <Core.Layout aria-live="polite" role="status" style={{ position: 'absolute', left: '-9999px' }}>
            {copySuccess === true && i18n.t('Copied successfully!')}
            {copySuccess === false && i18n.t('Copy unsuccessful, please copy manually')}
          </Core.Layout>
          {copySuccess === false && <span className="error"> {i18n.t('Copy unsuccessful, please copy manually')}</span>}
          <Core.Layout className="modal-logo">
            <img src={logo} alt={i18n.t('EVERFI logo')} />
          </Core.Layout>
        </Core.Layout>
      </Core.Layout>
    </Core.Modal>
  );
};
HelpModalBody.displayName = 'HelpModalBody';

export { HelpModalBody };
